import { defineQuiz } from "./_schema";
import type { Answers, PersonaKey, Question, RecapSegment } from "./_schema";
import {
  buildAbsolutionAccent,
  buildAbsolutionBody,
  buildMechanismAccent,
  buildMechanismBody,
  buildMechanismHeading,
  deriveCortisolWeightlossProfile,
  deriveCortisolWeightlossSummary,
} from "./cortisol-weightloss-profile";
import { MM_0102 } from "./_experiments";

/**
 * CORTISOL WEIGHT-LOSS QUIZ -- the cortisol angle pointed at the belly instead
 * of the mirror. Same mechanism as `cortisol` (cortisol high -> testosterone
 * suppressed), told through fat storage, stress-eating, and "nothing I try
 * moves the scale" rather than puffiness and brain fog alone.
 *
 * Ten single-choice screens (seven reused verbatim from `cortisol`: `age`,
 * `puffy-face`, `wake-pattern`, `tension`, `energy-3pm`, `libido`,
 * `when-started`; three new to this angle: `fat-location`, `tried`, and a
 * re-prompted `sugar-cravings`) + two bridges + TWO tally screens (`summary`,
 * both fed by the one `deriveSummary`) + the analyzing beat = 15 screens.
 *
 * Design spec: docs/specs/2026-09-04-cortisol-weightloss-quiz-design.md.
 *
 * Like `cortisol` and `quiz-v6`, this quiz has no GLP-1 screener, so
 * `glp1-user` is not reachable from the classifier. Unlike `cortisol`, this
 * quiz asks neither `body-composition` nor `brain-fog` -- `fat-location`
 * replaces the first slot, but only the DERIVER reads it (translated to
 * quiz-v6's `body-composition` shape via `quizzes/cortisol-weightloss-profile.ts`),
 * not the classifier. `classifier` is copied verbatim from `cortisol` below
 * regardless (classifiers are never authored per angle quiz), so the branches
 * keyed on `body-composition`/`brain-fog` read `undefined` and never fire:
 * `performance` and `aging-men` join `glp1-user` as unreachable here. The
 * three reachable personas are `trt-alternative`, `skeptical-buyer`, and
 * `everyday-man`.
 */

const QUESTION_ORDER = [
  "age",
  "fat-location",
  "tried",
  "sugar-cravings",
  "puffy-face",
  "info-absolution",
  "signs-so-far",
  "wake-pattern",
  "energy-3pm",
  "libido",
  "tension",
  "when-started",
  "info-mechanism",
  "signs-final",
  "info-analyzing",
] as const;

/**
 * Both bridge builders in `cortisol-weightloss-profile.ts` return plain
 * paragraphs (`string[]`) -- neither ever echoes the user's own words back to
 * him, so there is nothing to flag `customerProvided`. The `info` screen's
 * `deriveBody` seam is typed for the general case (paragraphs of
 * `RecapSegment`s, so a quiz that DOES echo an answer can highlight it, e.g.
 * quiz-beer's bridge 2) -- this wraps the plain paragraphs in that shape.
 *
 * `accent`, when given, is marked on its first occurrence in each paragraph
 * and renders in mars-orange. It is a parameter rather than a `bodyAccents`
 * entry on the screen because the phrase changes with the answers, so no
 * static substring could express it -- see the `RecapSegment` docstring in
 * `_schema.ts`. A span that occurs nowhere renders the paragraph plain and
 * throws nothing, which is why each accent builder derives its span from the
 * same literal as the paragraph it lives in, and why tests assert the two
 * still meet on every reachable count -- 0..4 for bridge 1, 0..8 for bridge 2,
 * which has seen all eight signs by the time it renders.
 */
function toParagraphs(
  lines: readonly string[],
  accent?: string,
): readonly (readonly RecapSegment[])[] {
  return lines.map((text) => {
    const at = accent === undefined ? -1 : text.indexOf(accent);
    if (accent === undefined || at === -1) return [{ text, customerProvided: false }];
    const segments: RecapSegment[] = [];
    if (at > 0) segments.push({ text: text.slice(0, at), customerProvided: false });
    segments.push({ text: accent, customerProvided: false, accent: true });
    const rest = text.slice(at + accent.length);
    if (rest) segments.push({ text: rest, customerProvided: false });
    return segments;
  });
}

const questions: Question[] = [
  {
    id: "age",
    type: "single",
    prompt: "How old are you?",
    options: [
      { value: "50-plus", label: "50+ years old" },
      { value: "40-49", label: "40–49 years old" },
      { value: "30-39", label: "30–39 years old" },
      { value: "20-29", label: "20–29 years old" },
    ],
  },
  {
    id: "fat-location",
    type: "single",
    prompt: "Where does your body hold onto fat the most?",
    options: [
      {
        value: "belly",
        label: "My belly, my waistline has grown",
        image: {
          src: "/static/images/cortisol-wl-fat-belly.webp",
          alt: "Illustration of a man with the fat highlighted around his belly",
        },
      },
      {
        value: "chest",
        label: "My chest and belly both",
        image: {
          src: "/static/images/cortisol-wl-fat-chest.webp",
          alt: "Illustration of a man with the fat highlighted across his chest and belly",
        },
      },
      {
        value: "handles",
        label: "My love handles and lower back",
        image: {
          src: "/static/images/cortisol-wl-fat-handles.webp",
          alt: "Illustration of a man with the fat highlighted at both flanks",
        },
      },
      {
        value: "everywhere",
        label: "Pretty evenly / not sure",
        image: {
          src: "/static/images/cortisol-wl-fat-everywhere.webp",
          alt: "Illustration of a man with the fat spread evenly over his torso",
        },
      },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      // UNSOURCED: this claim is in neither docs/t-suppression-research.md
      // nor docs/product-brief.md. Needs operator sign-off or a research-doc
      // entry before it can be treated as verified product/medical copy.
      body: "Deep belly fat is densely packed with cortisol receptors, which research suggests makes it especially responsive to the stress hormone.",
    },
  },
  {
    id: "tried",
    type: "single",
    prompt: "What have you tried to lose weight?",
    options: [
      { value: "clean-still", label: "I eat clean and train, and it still won't budge" },
      { value: "effort-gap", label: "I try, but results don't match the effort" },
      { value: "on-off", label: "I've tried diet and exercise on and off" },
      { value: "not-yet", label: "I haven't really tried yet" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body: "When fat holds on despite clean eating and hard training, the problem usually isn't effort. It's the signal telling your body to store it in the first place.",
    },
  },
  {
    id: "sugar-cravings",
    type: "single",
    // Reused id + values from cortisol.ts's sugar-cravings screen (the
    // deriver's tier/reported maps key on these values) -- only the prompt
    // and labels are re-aimed at stress-eating, per the design spec.
    prompt: "When you're stressed or stretched thin, what happens with food?",
    options: [
      { value: "daily", label: "I lose control, almost every day" },
      { value: "few-times-week", label: "It happens a few times a week" },
      { value: "occasionally", label: "Occasionally" },
      { value: "rarely", label: "Rarely -- stress doesn't change how I eat" },
    ],
  },
  {
    id: "puffy-face",
    type: "single",
    prompt: "When you look in the mirror, does your face look puffier or softer than it used to?",
    options: [
      { value: "yes-noticeably", label: "Yes, noticeably" },
      { value: "some-mornings", label: "Some mornings" },
      { value: "not-sure", label: "Not sure" },
      { value: "no", label: "No" },
    ],
  },
  {
    // BRIDGE 1 -- absolution. `deriveBody` supplies the reactive close; the
    // static `body` is the fallback a reader of this file sees. Deliberately
    // mechanism-silent (does not name testosterone's HOW) -- that is bridge
    // 2's payload five questions later, same split as live `cortisol`.
    //
    // AUDITED (same pass that added bridge 2's `deriveHeading`): this heading
    // stays static and does NOT need a reactive counterpart. Bridge 2's
    // heading ("Your signs point to one thing...") makes a specific claim
    // about THIS reader's data, directly falsified by "you reported none of
    // the eight" one line below it. "You're not imagining it" makes no claim
    // about his signs at all -- it validates that the underlying mechanism
    // (modern life keeps cortisol elevated) is real, which stays true whether
    // or not he personally has symptoms yet. `deriveBody`'s own zero bucket
    // already carries the acknowledgment ("So far it isn't showing up in the
    // places it usually shows up first"), and that sentence is good news
    // under this heading, not a contradiction of it.
    id: "info-absolution",
    type: "info",
    heading: "You're not imagining it.",
    body: [
      "You go to the gym, you eat clean, and the mirror still doesn't match the effort.",
      "That's not laziness, and it's not just getting older. Modern life puts your body under a constant, low-grade pressure it was never built for -- the kind that quietly overrides discipline and keeps the fat locked in place.",
      "Something else is running the show.",
    ],
    deriveBody: (answers) =>
      toParagraphs(buildAbsolutionBody(answers), buildAbsolutionAccent(answers)),
    cta: "Continue",
    // NO IMAGE, deliberately. `cortisol-signs.webp` was tried here as a
    // placeholder (spec open item 5: the "overdue-bills desk" photo never
    // landed) but it is a LABELLED eight-panel graphic whose own alt text
    // enumerates cortisol's eight: puffy face, 2-3 AM waking, tensed neck,
    // low energy, stubborn belly fat, sugar cravings, brain fog, low libido.
    // This quiz's eight (see SIGN_ROWS in cortisol-weightloss-profile.ts) are
    // different: belly fat, effort gap, cravings, puffy face, 2-3am wake, 3pm
    // crash, low drive, tension. Brain fog is shown and never asked; effort
    // gap is counted and never shown -- so the placeholder had the reader
    // count a different eight than the screen right after this one asks
    // about. A screen with no image renders correctly (rule 20); a screen
    // with a contradicting one does not. Restore an image here only once the
    // operator supplies artwork that enumerates THIS quiz's eight signs.
  },
  {
    id: "signs-so-far",
    type: "summary",
    eyebrow: "Your count so far",
    heading: "The eight signs, so far",
    helper: "Four asked, four to go. Each one you confirm tightens the read.",
    cta: "Check the rest",
  },
  {
    id: "wake-pattern",
    type: "single",
    prompt: "Do you wake up between 2 and 3 AM, for no reason?",
    options: [
      { value: "most-nights", label: "Most nights" },
      { value: "few-times-week", label: "A few times a week" },
      { value: "rarely", label: "Rarely" },
      { value: "never", label: "Never" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body: "Cortisol runs on a 24-hour rhythm. When it’s stuck high, it spikes in the early-morning hours. That’s the 2–3 AM wake-up.",
    },
  },
  {
    id: "energy-3pm",
    type: "single",
    prompt: "How’s your energy at 3pm compared to when you wake up?",
    options: [
      { value: "drained", label: "Completely drained, I need caffeine or a nap" },
      { value: "afternoon-slump", label: "Noticeably lower, afternoon slump hits hard" },
      { value: "slightly-tired", label: "Slightly tired, still functional but slower" },
      { value: "same-energy", label: "Same energy, no afternoon crash" },
    ],
  },
  {
    id: "libido",
    type: "single",
    prompt: "Has your drive dropped compared to a few years ago?",
    options: [
      { value: "significantly", label: "Significantly" },
      { value: "somewhat", label: "Somewhat" },
      { value: "not-sure", label: "Not sure" },
      { value: "no", label: "No" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body: "Testosterone and cortisol compete for real estate in your body. So when cortisol runs high, drive is usually the first thing to fade.",
    },
  },
  {
    id: "tension",
    type: "single",
    prompt: "Do you carry physical tension like a tight neck, stiff shoulders, clenched jaw?",
    options: [
      { value: "constantly", label: "Constantly" },
      { value: "most-days", label: "By the end of most days" },
      { value: "occasionally", label: "Occasionally" },
      { value: "rarely", label: "Rarely" },
    ],
  },
  {
    id: "when-started",
    type: "single",
    prompt: "When did you start noticing these changes?",
    options: [
      { value: "over-year", label: "Over a year ago" },
      { value: "past-year", label: "In the past year" },
      { value: "few-months", label: "In the past few months" },
      { value: "not-sure", label: "Not sure" },
    ],
  },
  {
    // BRIDGE 2 -- name the villain. The full mechanism lands here (not on
    // bridge 1), same split as live `cortisol`. Closes on the line
    // info-analyzing below pays off.
    //
    // `heading` is the static fallback a reader of this file sees;
    // `deriveHeading` supplies the reactive title actually rendered whenever
    // `answers` is available. At a count of zero, the static heading below
    // would sit directly above deriveBody's "You reported none of the eight"
    // -- a headline claiming his signs point somewhere, above a line saying
    // he has none. See `buildMechanismHeading` in
    // cortisol-weightloss-profile.ts.
    id: "info-mechanism",
    type: "info",
    heading: "Your signs point to one thing: cortisol is suppressing your testosterone.",
    deriveHeading: buildMechanismHeading,
    body: [
      "Cortisol is your body's alarm system, built for short bursts. A threat appears, it spikes, the threat passes, it drops. Modern life never lets it drop.",
      "Stuck high, it tells your body to store belly fat, hold water in your face, and pull back on the testosterone that would otherwise burn it off. That's why the diet and the gym haven't been enough.",
      "Lower the cortisol, free the testosterone. Let's see how yours is running.",
    ],
    deriveBody: (answers) =>
      toParagraphs(buildMechanismBody(answers), buildMechanismAccent(answers)),
    cta: "Continue",
    // PLACEHOLDER, pending the operator's own artwork (spec open item 5: a
    // high-vs-low cortisol comparison graphic). Nothing validates image
    // existence at parse time, so this points at cortisol's real,
    // thematically-adjacent mechanism diagram rather than a path that would
    // 404. Swap the src once the dedicated asset lands.
    image: {
      src: "/static/images/cortisol-mechanism.webp",
      alt: "How testosterone gets suppressed: the brain signals the testes, but SHBG locks up testosterone and estrogen conversion drains it, leaving less free testosterone in the body",
    },
  },
  {
    // THE CLOSING TALLY. Same grid as `signs-so-far`, every row now resolved --
    // and that contrast is the payoff: the halfway screen greys four rows out
    // and says "four to go", this one fills them in. One `deriveSummary` feeds
    // both; the screens differ only in chrome.
    //
    // Placed AFTER bridge 2 and immediately before the analyzing beat, so the
    // CTA tells the truth: only the ~5s loader stands between this button and
    // the verdict. Before bridge 2 it would promise the assessment and then
    // serve two more reading screens, at the point the reader is closest to
    // leaving. The order also puts the grid AFTER bridge 2's opening line
    // ("You reported 3 of the eight") -- prose states the number, the grid
    // shows which ones, which builds instead of repeating.
    //
    // It reads honestly at a count of zero too: a fully resolved grid showing
    // 0 of 8 is the setup the optimization verdict needs, where walking from
    // the last question straight into prose was not.
    //
    // No `helper`: the grid is the message, and this screen has to hold eight
    // rows plus the CTA on one phone fold (the CTA is pinned by the shared
    // StickyBar in SummaryDashboard, which is what fixed the halfway screen).
    id: "signs-final",
    type: "summary",
    eyebrow: "Your count",
    heading: "All eight, counted",
    cta: "CHECK YOUR ASSESSMENT",
  },
  {
    id: "info-analyzing",
    type: "info",
    // Pays off Bridge 2's closing line ("Let's see how yours is running").
    heading: "Analyzing your cortisol profile…",
    body: [
      "Counting the signs you just reported, and measuring how hard your cortisol is working against your testosterone.",
    ],
    animation: "analyzing",
    autoAdvanceMs: 5000,
  },
];

export const cortisolWeightlossQuiz = defineQuiz({
  id: "cortisol-weightloss",
  slug: "cortisol-weightloss",
  experiment: MM_0102,
  meta: {
    title: "What's Really Driving Your Stubborn Belly Fat",
    introHeadline: "Is high cortisol the reason your belly fat won't budge no matter what you try?",
    introAccent: "budge",
    introBody:
      "There are 8 signs high cortisol is storing fat you can't shift. See how many you have in this 2 minute quiz.",
    // No `testimonial`: no congruent review exists for this angle yet (spec
    // open item 4). Omitting the field is the endorsed pattern (rule 19) --
    // wrong-angle proof is worse than none, and ten quizzes already share one
    // generic portrait; this does not add an eleventh.
    // MM-0102 -- see quizzes/_experiments.ts.
    introVariantTag: "control",
  },
  questions,

  branching: (_answers: Answers, currentId: string): string | null => {
    const i = QUESTION_ORDER.indexOf(currentId as (typeof QUESTION_ORDER)[number]);
    if (i === -1 || i === QUESTION_ORDER.length - 1) return null;
    return QUESTION_ORDER[i + 1]!;
  },

  // Copied VERBATIM from quizzes/cortisol.ts -- classifiers are not authored
  // per angle quiz. `body-composition` and `brain-fog` are cortisol screens
  // this quiz does not ask (fat-location replaces the first slot, but only
  // the DERIVER reads it -- see cortisol-weightloss-profile.ts), so both
  // read `undefined` here and the branches keyed on them never fire:
  // `performance` and `aging-men` join `glp1-user` as unreachable on this
  // quiz. The three reachable personas are `trt-alternative`,
  // `skeptical-buyer`, and `everyday-man`.
  classifier(answers: Answers): PersonaKey {
    const age = answers["age"];
    const body = answers["body-composition"];
    const energy = answers["energy-3pm"];
    const onset = answers["when-started"];
    const libido = answers["libido"];
    const fog = answers["brain-fog"];

    // Performance-focused: lean body chasing a cognitive/energy edge.
    if (body === "lean-muscular" && (fog === "daily" || fog === "most-days" || energy === "drained")) {
      return "performance";
    }

    // TRT-alternative: long-running collapse across energy + drive.
    if (onset === "over-year" && energy === "drained" && (libido === "significantly" || age === "50-plus")) {
      return "trt-alternative";
    }

    // Skeptical buyer: can't place when it started.
    if (onset === "not-sure") {
      return "skeptical-buyer";
    }

    // Aging men: 40+ with visible softening.
    if ((age === "50-plus" || age === "40-49") && (body === "soft-some-muscle" || body === "overweight")) {
      return "aging-men";
    }

    return "everyday-man";
  },

  deriveProfile: deriveCortisolWeightlossProfile,
  deriveSummary: deriveCortisolWeightlossSummary,
});
